import { openDB, type IDBPDatabase } from 'idb';
import type { NewEvent } from '@ilteducation/activity-stream-types';

/**
 * Persistent FIFO queue for activity-stream events.
 *
 * The buffer of un-sent events can grow large enough to blow the tight localStorage
 * quota (especially while offline), so we prefer IndexedDB for its far larger budget.
 * IndexedDB keeps events ordered for us: the object store uses `autoIncrement` for
 * unique keys and a `byTimestamp` index so the oldest event is always sent first,
 * even when batches arrive with overlapping timestamp intervals.
 *
 * There are corner cases where IndexedDB is not available, though — e.g. Safari
 * strips the API in Lockdown Mode and in some sandboxed/cross-origin iframe contexts,
 * where even referencing `indexedDB` throws a ReferenceError. When it is absent we
 * fall back to an in-memory queue: the app keeps working and events still flush while
 * the tab is open; only events left un-sent when the tab closes are lost. This is a
 * rare, best-effort degradation, not the primary path.
 *
 * See: https://ilt-education.sentry.io/issues/128399048/
 *
 * Either way events are normalised through JSON before storage (see
 * {@link toStoredRecord}) and handed back as the pristine {@link NewEvent}.
 */

const DB_NAME = 'activity-stream-persisted-events';
const STORE_NAME = 'events';
const TIMESTAMP_INDEX = 'byTimestamp';

/**
 * The stored record. The wire-shaped {@link NewEvent} is kept verbatim under
 * `event`; `timestampMs` is ordering metadata only.
 */
interface PersistedEvent {
  event: NewEvent;
  timestampMs: number;
}

interface QueueStorage {
  enqueue(events: NewEvent[]): Promise<void>;
  pop(batchSize: number): Promise<NewEvent[]>;
  count(): Promise<number>;
}

/**
 * Normalises an event timestamp to epoch milliseconds, used for ordering.
 *
 * Falls back to "now" when a timestamp is absent or invalid (matching the previous
 * behaviour of sorting such events last). On IndexedDB this is also the index key,
 * and IndexedDB skips records whose indexed field is missing, so it must always be
 * a keyable number.
 */
const toMs = (timestamp: NewEvent['timestamp']): number => {
  if (!timestamp) {
    return Date.now();
  }
  // In the NewEvent type, timestamps are defined as Date, but in reality is string, when serialized
  const ms = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp.getTime();
  return Number.isNaN(ms) ? Date.now() : ms;
};

/**
 * Builds a stored record from an event.
 *
 * The event is round-tripped through JSON to produce a plain, structured-clone-safe
 * object. IndexedDB stores values with the structured clone algorithm, which (unlike
 * the previous `JSON.stringify` localStorage path) throws `DataCloneError` on things
 * like Vue reactive proxies, functions or class instances that callers may pass in.
 * JSON normalisation strips exactly those, and matches the shape the event is sent to
 * the server in anyway.
 */
const toStoredRecord = (event: NewEvent): PersistedEvent => ({
  event: JSON.parse(JSON.stringify(event)) as NewEvent,
  timestampMs: toMs(event.timestamp),
});

const isIndexedDbAvailable = (): boolean => {
  try {
    return typeof indexedDB !== 'undefined' && !!indexedDB;
  } catch {
    // `typeof` shouldn't throw, but accessing a stripped global can in some cases.
    return false;
  }
};

let dbPromise: Promise<IDBPDatabase> | null = null;

const getDb = (): Promise<IDBPDatabase> => {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, 1, {
      upgrade(db) {
        const store = db.createObjectStore(STORE_NAME, { autoIncrement: true });
        store.createIndex(TIMESTAMP_INDEX, 'timestampMs');
      },
    });
  }
  return dbPromise;
};

const indexedDbStorage: QueueStorage = {
  async enqueue(events) {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    await Promise.all([...events.map((event) => tx.store.add(toStoredRecord(event))), tx.done]);
  },

  // Atomically reads and removes the oldest `batchSize` events (by timestamp), in a
  // single transaction so a concurrent enqueue can't interleave between read and delete.
  async pop(batchSize) {
    const db = await getDb();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const index = tx.store.index(TIMESTAMP_INDEX);

    const ids = await index.getAllKeys(null, batchSize);
    const records = (await index.getAll(null, batchSize)) as PersistedEvent[];

    await Promise.all([...ids.map((id) => tx.store.delete(id)), tx.done]);

    return records.map((record) => record.event);
  },

  async count() {
    const db = await getDb();
    return db.count(STORE_NAME);
  },
};

// In-memory fallback for when IndexedDB is unavailable.
const memoryQueue: PersistedEvent[] = [];

const inMemoryStorage: QueueStorage = {
  async enqueue(events) {
    memoryQueue.push(...events.map(toStoredRecord));
    // Keep ordered by timestamp so `pop` takes the oldest from the front.
    memoryQueue.sort((a, b) => a.timestampMs - b.timestampMs);
  },

  async pop(batchSize) {
    return memoryQueue.splice(0, batchSize).map((record) => record.event);
  },

  async count() {
    return memoryQueue.length;
  },
};

let storage: QueueStorage | null = null;

const getStorage = (): QueueStorage => {
  if (!storage) {
    storage = isIndexedDbAvailable() ? indexedDbStorage : inMemoryStorage;
  }
  return storage;
};

/** Appends events to the queue. */
export const enqueueEvents = async (events: NewEvent[]): Promise<void> => {
  if (events.length === 0) {
    return;
  }
  await getStorage().enqueue(events);
};

/** Reads and removes the oldest `batchSize` events (by timestamp) to send. */
export const popBatch = (batchSize: number): Promise<NewEvent[]> => getStorage().pop(batchSize);

/** Number of events currently buffered. */
export const countEvents = (): Promise<number> => getStorage().count();
